import React, { RefObject, useEffect, useState } from "react";
import { TickIcon } from "./TickIcon";
import { Message } from "../api/types";
import "../styles/ChatMessages.css";

type Props = {
  messages: Message[];
  messagesEndRef: RefObject<HTMLDivElement>;
  otherUserTyping: boolean;
  myName?: string;
  otherName?: string;
};

const HAS_HAD_MESSAGES_KEY = "idless_has_had_messages";

const ChatMessages: React.FC<Props> = ({
  messages,
  messagesEndRef,
  otherUserTyping,
  myName,
  otherName,
}) => {
  const [hasHadMessages, setHasHadMessages] = useState<boolean>(() => {
    return sessionStorage.getItem(HAS_HAD_MESSAGES_KEY) === "true";
  });

  // ✅ Once messages appear, remember it
  useEffect(() => {
    if (messages.length > 0 && !hasHadMessages) {
      setHasHadMessages(true);
      sessionStorage.setItem(HAS_HAD_MESSAGES_KEY, "true");
    }
  }, [messages, hasHadMessages]);

  return (
    <div className="messages-container">
      {/* ✅ Only show on true "first ever empty state" */}
      {messages.length === 0 && !hasHadMessages && (
        <p className="no-messages">No messages yet. Start the conversation!</p>
      )}
      {messages.map((message, index) => {
        const isSent = message.sender === "sent";
        // Name only on the first message of a run from the same sender
        const isRunStart = messages[index - 1]?.sender !== message.sender;
        const isRunEnd = messages[index + 1]?.sender !== message.sender;
        const name = (isSent ? myName : otherName)?.trim();

        return (
          <div
            key={message.id}
            className={`${
              isSent ? "message-sent-wrapper" : "message-received-wrapper"
            }${isRunEnd ? "" : " message-run-continues"}`}
          >
            {isRunStart && name && (
              <div className="message-sender-name">{name}</div>
            )}
            <div className={isSent ? "message-sent" : "message-received"}>
              {message.text}
            </div>

            {isSent && (
              <div className="message-ticks" aria-label={message.status}>
                {message.status === "failed" ? (
                  <span className="message-failed">!</span>
                ) : (
                  <>
                    <TickIcon className="tick delivered" />

                    {message.status === "delivered" && (
                      <TickIcon className="tick delivered" />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
      {otherUserTyping && (
        <div className="typing-message-row" aria-label="Other user is typing">
          <div className="typing-dots-bubble">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
